import {AUTH_MODE, AUTH_SUB_TYPE, AUTH_TYPE} from '@Libraries/login-signup-library';
import type {SavedLoginAccount} from '@Libraries/login-signup-library';
import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import {loginPageExtraReducers} from '@Components/login-page/login-page-extra-reducers';
import type {AuthTypes, ConfirmLoginState, ErrorNotificationData, LoginPageInitializeProps, LoginPageState} from '@Components/login-page/login-page.types';

const initialState: LoginPageState = {
  authType: AUTH_TYPE.LOGIN,
  authMode: AUTH_MODE.DEFAULT,
  errorMessage: '',
  showError: false,
  areFieldsMarkedInvalid: false,
  isInDataRegulatedLocation: false,
  areAuthButtonsDisabled: true,
  isSignupBlocked: false,
  isRecaptchaLoaded: false,
  animateNotificationBanner: false,
  loginConfirmationToken: '',
  pendingLoginEmail: '',
  pendingLoginPassword: '',
  projectName: '',
  confirmLoginType: AUTH_TYPE.LOGIN,
  twoFactorVerificationOrigin: AUTH_TYPE.LOGIN,
  signupSource: 'default',
  signupActionType: '',
  loginActionType: '',
  showAuthForm: false,
  signupEmail: '',
  forgotPasswordEmail: '',
  authSubType: AUTH_SUB_TYPE.LOGIN_WITHOUT_FORM,
  isLoading: false,
  redirectUrl: '',
  isLoginToProceedMessage: false,
  isConnectSSOAccountModal: false,
  inactiveUserPasswordResetSniperLink: '',
  savedAccounts: [],
  selectedSavedAccountHashedId: null,
  isRemovingSavedAccounts: false,
};

export const LoginPageSlice = createSlice({
  name: 'loginPage',
  initialState,
  reducers: {
    selectSavedAccount: (state, action: PayloadAction<string>) => {
      state.selectedSavedAccountHashedId = action.payload;
    },
    startRemovingSavedAccounts: (state) => {
      state.isRemovingSavedAccounts = true;
    },
    stopRemovingSavedAccounts: (state) => {
      state.isRemovingSavedAccounts = false;
    },
    setSavedAccounts: (state, action: PayloadAction<SavedLoginAccount[]>) => {
      state.savedAccounts = action.payload;
    },
    updateSignupEmail: (state, action: PayloadAction<string>) => {
      state.signupEmail = action.payload;
    },
    updateForgotPasswordEmail: (state, action: PayloadAction<string>) => {
      state.forgotPasswordEmail = action.payload;
    },
    initializeLoginPageState: (state, action: PayloadAction<LoginPageInitializeProps>) => {
      state.errorMessage = action.payload.errorMessage;
      state.showError = action.payload.showError;
      state.areFieldsMarkedInvalid = action.payload.showError;
      state.isInDataRegulatedLocation = action.payload.isInDataRegulatedLocation;
      state.signupActionType = action.payload.signupActionType;
      state.loginActionType = action.payload.loginActionType;
      state.redirectUrl = action.payload.redirectUrl;
      state.isLoginToProceedMessage = action.payload.isLoginToProceedMessage;
      state.savedAccounts = action.payload.savedAccounts ?? [];
      // The store outlives the auth modal, so nothing one visit chose or typed survives into the next.
      state.selectedSavedAccountHashedId = initialState.selectedSavedAccountHashedId;
      state.isRemovingSavedAccounts = initialState.isRemovingSavedAccounts;
      state.signupEmail = initialState.signupEmail;
      state.forgotPasswordEmail = initialState.forgotPasswordEmail;

      if (action.payload.initialAuthType !== undefined) {
        state.authType = action.payload.initialAuthType;
      } else if (action.payload.isOAuthFlow) {
        state.authType = AUTH_TYPE.SIGNUP;
      } else if (state.savedAccounts.length > 0) {
        state.authType = AUTH_TYPE.SAVED_ACCOUNTS;
      }
    },
    updateAuthTypeState: (state, action: PayloadAction<AuthTypes>) => {
      if (action.payload.type === AUTH_TYPE.TWO_FACTOR_VERIFICATION) {
        state.twoFactorVerificationOrigin = state.authType;
      }

      state.authType = action.payload.type;
      if (action.payload.subType !== undefined) {
        state.authSubType = action.payload.subType;
      }
    },
    updateAuthMode: (state, action: PayloadAction<AUTH_MODE>) => {
      state.authMode = action.payload;
    },
    updateErrors: (state, action: PayloadAction<ErrorNotificationData>) => {
      if (state.isLoginToProceedMessage) {
        state.isLoginToProceedMessage = false;
      }
      state.errorMessage = action.payload.errorMessage;
      state.showError = action.payload.showError;
      state.areFieldsMarkedInvalid = action.payload.showError;
      state.animateNotificationBanner = action.payload.animateNotificationBanner;
    },
    clearFieldErrorMarks: (state) => {
      state.areFieldsMarkedInvalid = false;
    },
    updateSignupSource: (state, action: PayloadAction<string>) => {
      state.signupSource = action.payload;
    },
    enableAuthButtonsAccess: (state) => {
      state.areAuthButtonsDisabled = false;
    },
    blockSignup: (state) => {
      state.isSignupBlocked = true;
    },
    setRecaptchaLoaded: (state) => {
      state.isRecaptchaLoaded = true;
    },
    resetNotificationBannerAnimation: (state) => {
      state.animateNotificationBanner = false;
    },
    enableLoadingState: (state) => {
      state.isLoading = true;
    },
    disableLoadingState: (state) => {
      state.isLoading = false;
    },
    switchToConfirmLogin: (state, action: PayloadAction<ConfirmLoginState>) => {
      state.confirmLoginType = action.payload.confirmLoginType;
      state.loginConfirmationToken = action.payload.loginConfirmationToken;
      state.pendingLoginEmail = action.payload.pendingLoginEmail;
      state.pendingLoginPassword = action.payload.pendingLoginPassword;
      state.projectName = action.payload.projectName;
      state.authType = AUTH_TYPE.CONFIRM_LOGIN;
    },
    setIsConnectSSOAccountModal: (state) => {
      state.isConnectSSOAccountModal = true;
    },
    setIsNotConnectSSOAccountModal: (state) => {
      state.isConnectSSOAccountModal = false;
    },
    setInactiveUserPasswordResetSniperLink: (state, action: PayloadAction<string>) => {
      state.inactiveUserPasswordResetSniperLink = action.payload;
    },
  },

  extraReducers: loginPageExtraReducers,
});

export const {
  initializeLoginPageState,
  updateAuthTypeState,
  updateErrors,
  clearFieldErrorMarks,
  enableAuthButtonsAccess,
  blockSignup,
  setRecaptchaLoaded,
  resetNotificationBannerAnimation,
  switchToConfirmLogin,
  updateAuthMode,
  updateSignupSource,
  enableLoadingState,
  disableLoadingState,
  setIsConnectSSOAccountModal,
  setIsNotConnectSSOAccountModal,
  setInactiveUserPasswordResetSniperLink,
  updateSignupEmail,
  updateForgotPasswordEmail,
  selectSavedAccount,
  startRemovingSavedAccounts,
  stopRemovingSavedAccounts,
  setSavedAccounts,
} = LoginPageSlice.actions;

export const loginPageReducer = LoginPageSlice.reducer;